import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  ChevronLeft,
  Save,
  Plus,
  Trash2,
  Sparkles,
  Layers,
  Loader2,
  CheckCircle2,
  X,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const newId = () => Math.random().toString(36).slice(2, 10);

export default function FrameworkBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = hasRole(user, "tenant_admin", "hr_admin");
  const [fw, setFw] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    api.get(`/frameworks/${id}`).then((r) => setFw(r.data));
  }, [id]);

  if (!fw) return <div className="text-sm text-slate-500">Loading…</div>;

  const isPublished = fw.status === "published";
  const readonly = isPublished || !isAdmin;

  const update = (patch) => setFw({ ...fw, ...patch });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: fw.name,
        description: fw.description,
        objective_fields: fw.objective_fields,
        classification: fw.classification,
        rating_scale: fw.rating_scale,
        values_assessment: fw.values_assessment,
        cycle_phases: fw.cycle_phases,
        weightage_rules: fw.weightage_rules,
        final_rating_composition: fw.final_rating_composition,
      };
      const r = await api.patch(`/frameworks/${id}`, payload);
      setFw(r.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      await api.post(`/frameworks/${id}/publish`);
      toast.success("Published");
      navigate(`/frameworks/${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="max-w-5xl space-y-6" data-testid="framework-builder-page">
      <Link
        to={`/frameworks/${id}`}
        className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5"
      >
        <ChevronLeft className="w-3 h-3" /> Back to detail
      </Link>

      <div className="axia-card p-6 lg:p-8 flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-md bg-slate-900 text-white flex items-center justify-center">
            <Layers className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-[280px]">
            <span
              className={`axia-pill ${
                isPublished
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-amber-50 text-amber-700 border-amber-200"
              }`}
            >
              {isPublished ? <CheckCircle2 className="w-3 h-3" /> : null}
              {fw.status}
            </span>
            <Input
              className="mt-2 font-display text-2xl font-bold tracking-tight border-0 px-0 focus-visible:ring-0"
              value={fw.name}
              disabled={readonly}
              onChange={(e) => update({ name: e.target.value })}
              data-testid="fw-name"
            />
            <Textarea
              className="mt-2 text-sm text-slate-500 border-0 px-0 focus-visible:ring-0 resize-none"
              rows={2}
              value={fw.description || ""}
              disabled={readonly}
              onChange={(e) => update({ description: e.target.value })}
              data-testid="fw-description"
              placeholder="Short description shown on the framework card"
            />
          </div>
        </div>
        {!readonly && (
          <div className="flex items-center gap-2">
            <Button onClick={save} disabled={saving} variant="outline" data-testid="fw-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-2" />}
              Save draft
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button disabled={publishing} className="bg-slate-900" data-testid="fw-publish">
                  {publishing && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />}
                  Publish
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="publish-confirm-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Publish this framework?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Publishing locks the framework. To make further changes you'll need to clone it.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="cancel-publish">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={publish} data-testid="confirm-publish">
                    Publish
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>

      {isPublished && (
        <div className="axia-card border-amber-200 bg-amber-50/50 p-4 text-sm text-amber-900">
          This framework is <strong>published</strong> and immutable. Clone it to make changes.
        </div>
      )}

      {/* Objective fields */}
      <Section title="Objective fields" subtitle="Fields employees fill in for each objective">
        <div className="space-y-3">
          {(fw.objective_fields || []).map((f, idx) => (
            <ObjectiveFieldRow
              key={f.id || idx}
              field={f}
              readonly={readonly}
              onChange={(v) => {
                const arr = [...fw.objective_fields];
                arr[idx] = v;
                update({ objective_fields: arr });
              }}
              onRemove={() => {
                const arr = fw.objective_fields.filter((_, i) => i !== idx);
                update({ objective_fields: arr });
              }}
            />
          ))}
          {!readonly && (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                update({
                  objective_fields: [
                    ...(fw.objective_fields || []),
                    { id: newId(), label: "New field", type: "textarea", required: true, help_text: "" },
                  ],
                })
              }
              data-testid="add-objective-field"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add field
            </Button>
          )}
        </div>
      </Section>

      {/* Rating scale */}
      <Section title="Rating scale" subtitle="The values managers and employees pick from">
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {(fw.rating_scale?.levels || []).map((l, idx) => (
              <RatingLevelCard
                key={idx}
                level={l}
                readonly={readonly}
                onChange={(v) => {
                  const arr = [...fw.rating_scale.levels];
                  arr[idx] = v;
                  update({ rating_scale: { ...fw.rating_scale, levels: arr } });
                }}
                onRemove={() => {
                  const arr = fw.rating_scale.levels.filter((_, i) => i !== idx);
                  update({ rating_scale: { ...fw.rating_scale, levels: arr } });
                }}
              />
            ))}
          </div>
          {!readonly && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const arr = [
                  ...(fw.rating_scale?.levels || []),
                  { value: 1, label: "New level", description: "" },
                ];
                update({ rating_scale: { type: fw.rating_scale?.type || "numeric", levels: arr } });
              }}
              data-testid="add-rating-level"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add level
            </Button>
          )}
        </div>
      </Section>

      {/* Values assessment */}
      <Section
        title="Values assessment"
        subtitle="Optional values + behaviours scored alongside objectives"
        right={
          <div className="flex items-center gap-2">
            <Label htmlFor="va-toggle" className="text-xs text-slate-500">Enabled</Label>
            <Switch
              id="va-toggle"
              checked={!!fw.values_assessment?.enabled}
              disabled={readonly}
              onCheckedChange={(v) =>
                update({
                  values_assessment: { ...(fw.values_assessment || { values: [] }), enabled: v },
                })
              }
              data-testid="values-toggle"
            />
          </div>
        }
      >
        {fw.values_assessment?.enabled && (
          <div className="space-y-3">
            {(fw.values_assessment.values || []).map((v, idx) => (
              <ValueRow
                key={v.id || idx}
                value={v}
                readonly={readonly}
                onChange={(next) => {
                  const arr = [...fw.values_assessment.values];
                  arr[idx] = next;
                  update({ values_assessment: { ...fw.values_assessment, values: arr } });
                }}
                onRemove={() => {
                  const arr = fw.values_assessment.values.filter((_, i) => i !== idx);
                  update({ values_assessment: { ...fw.values_assessment, values: arr } });
                }}
              />
            ))}
            {!readonly && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  update({
                    values_assessment: {
                      ...fw.values_assessment,
                      values: [
                        ...fw.values_assessment.values,
                        { id: newId(), name: "New value", description: "", expected_behaviours: [] },
                      ],
                    },
                  })
                }
                data-testid="add-value"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add value
              </Button>
            )}
          </div>
        )}
      </Section>

      {/* Composition / weightage */}
      <Section title="Final rating composition">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Objective weight (%)</Label>
            <Input
              type="number"
              min={0}
              max={100}
              value={fw.final_rating_composition?.objective_weight_pct ?? 100}
              disabled={readonly}
              onChange={(e) =>
                update({
                  final_rating_composition: {
                    ...fw.final_rating_composition,
                    objective_weight_pct: Number(e.target.value || 0),
                  },
                })
              }
              data-testid="objective-weight-pct"
            />
          </div>
          <div>
            <Label>Values weight (%)</Label>
            <Input
              type="number"
              min={0}
              max={100}
              value={fw.final_rating_composition?.values_weight_pct ?? 0}
              disabled={readonly}
              onChange={(e) =>
                update({
                  final_rating_composition: {
                    ...fw.final_rating_composition,
                    values_weight_pct: Number(e.target.value || 0),
                  },
                })
              }
              data-testid="values-weight-pct"
            />
          </div>
        </div>
      </Section>

      {/* Weightage rules */}
      <Section title="Objective weightage rules">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-1">
            <Label className="text-xs">Must sum to 100</Label>
            <Switch
              className="mt-2 block"
              checked={!!fw.weightage_rules?.must_sum_to_100}
              disabled={readonly}
              onCheckedChange={(v) =>
                update({ weightage_rules: { ...fw.weightage_rules, must_sum_to_100: v } })
              }
              data-testid="must-sum-100"
            />
          </div>
          <div>
            <Label>Min objectives</Label>
            <Input
              type="number"
              min={1}
              value={fw.weightage_rules?.min_objectives ?? 1}
              disabled={readonly}
              onChange={(e) =>
                update({
                  weightage_rules: { ...fw.weightage_rules, min_objectives: Number(e.target.value || 1) },
                })
              }
              data-testid="min-objectives"
            />
          </div>
          <div>
            <Label>Max objectives</Label>
            <Input
              type="number"
              min={1}
              value={fw.weightage_rules?.max_objectives ?? 8}
              disabled={readonly}
              onChange={(e) =>
                update({
                  weightage_rules: { ...fw.weightage_rules, max_objectives: Number(e.target.value || 1) },
                })
              }
              data-testid="max-objectives"
            />
          </div>
          <div>
            <Label>Per-objective min weight</Label>
            <Input
              type="number"
              min={0}
              value={fw.weightage_rules?.per_objective_min_weight ?? 5}
              disabled={readonly}
              onChange={(e) =>
                update({
                  weightage_rules: { ...fw.weightage_rules, per_objective_min_weight: Number(e.target.value || 0) },
                })
              }
              data-testid="per-objective-min"
            />
          </div>
        </div>
      </Section>
    </div>
  );
}

function Section({ title, subtitle, right, children }) {
  return (
    <section className="axia-card p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-base font-semibold text-slate-900">{title}</h2>
          {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function ObjectiveFieldRow({ field, onChange, onRemove, readonly }) {
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-white space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
        <div className="md:col-span-5">
          <Label className="text-[11px]">Label</Label>
          <Input
            value={field.label}
            disabled={readonly}
            onChange={(e) => onChange({ ...field, label: e.target.value })}
            data-testid={`field-label-${field.id}`}
          />
        </div>
        <div className="md:col-span-3">
          <Label className="text-[11px]">Type</Label>
          <Select
            value={field.type}
            disabled={readonly}
            onValueChange={(v) => onChange({ ...field, type: v })}
          >
            <SelectTrigger data-testid={`field-type-${field.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="text">Single line</SelectItem>
              <SelectItem value="textarea">Long text</SelectItem>
              <SelectItem value="number">Number</SelectItem>
              <SelectItem value="date">Date</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-3">
          <Label className="text-[11px]">ID (slug)</Label>
          <Input
            value={field.id}
            disabled={readonly}
            onChange={(e) => onChange({ ...field, id: e.target.value })}
          />
        </div>
        <div className="md:col-span-1 flex items-end justify-between gap-2">
          <div>
            <Label className="text-[11px]">Req</Label>
            <Switch
              className="mt-1.5"
              checked={!!field.required}
              disabled={readonly}
              onCheckedChange={(v) => onChange({ ...field, required: v })}
            />
          </div>
          {!readonly && (
            <Button
              size="icon"
              variant="ghost"
              onClick={onRemove}
              className="h-8 w-8 text-slate-400 hover:text-rose-600"
              data-testid={`remove-field-${field.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>
      <div>
        <Label className="text-[11px]">Help text</Label>
        <Input
          value={field.help_text || ""}
          disabled={readonly}
          onChange={(e) => onChange({ ...field, help_text: e.target.value })}
          placeholder="Shown under the field"
        />
      </div>
    </div>
  );
}

function RatingLevelCard({ level, onChange, onRemove, readonly }) {
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-white space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <Label className="text-[11px]">Value</Label>
          <Input
            type="number"
            step="0.1"
            value={level.value}
            disabled={readonly}
            onChange={(e) => onChange({ ...level, value: Number(e.target.value) })}
          />
        </div>
        {!readonly && (
          <Button
            size="icon"
            variant="ghost"
            onClick={onRemove}
            className="h-8 w-8 text-slate-400 hover:text-rose-600 ml-2 mt-4"
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
      <div>
        <Label className="text-[11px]">Label</Label>
        <Input
          value={level.label}
          disabled={readonly}
          onChange={(e) => onChange({ ...level, label: e.target.value })}
        />
      </div>
      <div>
        <Label className="text-[11px]">Description</Label>
        <Input
          value={level.description || ""}
          disabled={readonly}
          onChange={(e) => onChange({ ...level, description: e.target.value })}
        />
      </div>
    </div>
  );
}

function ValueRow({ value, onChange, onRemove, readonly }) {
  const behaviours = (value.expected_behaviours || []).join("\n");
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-white space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <Label className="text-[11px] flex items-center gap-1.5">
              <Sparkles className="w-3 h-3 text-violet-600" /> Name
            </Label>
            <Input
              value={value.name}
              disabled={readonly}
              onChange={(e) => onChange({ ...value, name: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-[11px]">ID (slug)</Label>
            <Input
              value={value.id}
              disabled={readonly}
              onChange={(e) => onChange({ ...value, id: e.target.value })}
            />
          </div>
        </div>
        {!readonly && (
          <Button
            size="icon"
            variant="ghost"
            onClick={onRemove}
            className="h-8 w-8 text-slate-400 hover:text-rose-600 mt-4"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
      <div>
        <Label className="text-[11px]">Description</Label>
        <Textarea
          rows={2}
          value={value.description || ""}
          disabled={readonly}
          onChange={(e) => onChange({ ...value, description: e.target.value })}
        />
      </div>
      <div>
        <Label className="text-[11px]">Expected behaviours · one per line</Label>
        <Textarea
          rows={3}
          value={behaviours}
          disabled={readonly}
          onChange={(e) =>
            onChange({
              ...value,
              expected_behaviours: e.target.value
                .split("\n")
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
        />
      </div>
    </div>
  );
}
